import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReportHistoryTimeline from 'dashboard/components/ReportHistoryTimeline';
import ReportRecurStatusSticker from 'dashboard/components/ReportRecurStatusSticker';
import ReportStatusSticker from 'dashboard/components/ReportStatusSticker';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { RECURRING_REPORT_TYPES, Report } from 'dashboard/models/report';
import { ReportRun } from 'dashboard/models/report-run';
import { ReportTemplate } from 'dashboard/models/report-template';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

export interface ReportInfoProps {
  report: Report;
  reportRun?: ReportRun;
  reportTemplate?: ReportTemplate;
}

const ReportInfo = ({ report, reportRun, reportTemplate }: ReportInfoProps) => {
  const {
    'reference-id': referenceId,
    'created-at': createdAt,
    'completed-at': completedAt,
    'next-scheduled-at': nextScheduledAt,
    'creator-name': creatorName,
  } = report.attributes;
  const createdAtFormatted = `${DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_TZ_FORMAT)}`;
  const completedAtFormatted = `${DateTime.fromISO(completedAt).toFormat(DATE_AND_TIME_TZ_FORMAT)}`;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const nextScheduledAtFormatted = `${DateTime.fromISO(nextScheduledAt).toFormat(
    DATE_AND_TIME_TZ_FORMAT,
  )}`;

  const accountId = report.relationships['account']?.data?.id;

  const isRecurringReport = RECURRING_REPORT_TYPES.includes(report.type);
  const showCustomListHistory = completedAt != null && report.type === ApiModel.ReportCustomList;

  const [openRunHistoryModal, { enable: showRunHistoryModal, disable: hideRunHistoryModal }] =
    useToggle(false);

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>Info</Card.Title>
          <InfoGrid>
            <Attribute label='Report ID' value={report.id} redacted={false} placeholder='' />
            <Attribute
              label='Template'
              value={
                reportTemplate ? (
                  <Link to={`/dashboard/report-templates/${reportTemplate.id}`}>
                    {reportTemplate.attributes?.name}
                  </Link>
                ) : null
              }
              redacted={false}
              placeholder='No template'
            />
            <Attribute
              label={reportRun ? 'Report Run Status' : 'Report Status'}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={<ReportStatusSticker report={report} reportRun={reportRun} />}
              placeholder=''
            />
            {isRecurringReport && (
              <Attribute
                label='Continuous Monitoring'
                value={<ReportRecurStatusSticker report={report} />}
                placeholder=''
              />
            )}
            <Attribute
              label='Reference ID'
              value={referenceId}
              redacted={false}
              placeholder='No reference ID'
            />
            <Attribute
              label='Account ID'
              value={
                accountId ? <Link to={`/dashboard/accounts/${accountId}`}>{accountId}</Link> : null
              }
              redacted={false}
              placeholder='No account ID'
            />
            {creatorName && (
              <Attribute
                label='Created by'
                value={creatorName}
                redacted={false}
                placeholder='N/A'
              />
            )}
            <Attribute
              label='Created at'
              value={createdAtFormatted}
              redacted={false}
              placeholder='Unknown creation date'
            />
            {completedAt != null && (
              <>
                {nextScheduledAt != null ? (
                  <>
                    <Attribute
                      label='Last updated at'
                      value={completedAtFormatted}
                      redacted={false}
                      placeholder='Unknown updated date'
                    />
                    <Attribute
                      label='Next update scheduled at'
                      value={nextScheduledAtFormatted}
                      redacted={false}
                      placeholder='Unknown next update scheduled date'
                    />
                  </>
                ) : (
                  <Attribute
                    label='Completed at'
                    value={completedAtFormatted}
                    redacted={false}
                    placeholder='Unknown completed date'
                  />
                )}
              </>
            )}
            {(isRecurringReport || showCustomListHistory) && (
              <>
                <ModalWrapper isOpen={openRunHistoryModal} handleCloseModal={hideRunHistoryModal}>
                  <ModalContainer size='xl'>
                    <ModalHeader onClose={hideRunHistoryModal}>Report History</ModalHeader>
                    <ModalBody>
                      <ReportHistoryTimeline
                        report={report}
                        linkToRuns={report.type === ApiModel.ReportCustomList}
                      />
                    </ModalBody>
                  </ModalContainer>
                </ModalWrapper>
                <HistoryButton
                  id='report-details-info__show-run-history'
                  onClick={showRunHistoryModal}
                >
                  Report History
                </HistoryButton>
              </>
            )}
          </InfoGrid>
        </Card.Body>
      </Card>
    </>
  );
};

const InfoGrid = styled(AttributeGrid)`
  grid-row-gap: ${Spacing[6]};
  grid-template-columns: auto;
`;

const HistoryButton = styled('button')`
  cursor: pointer;
  color: ${({ theme }) => theme.color.text.brand};
  background-color: transparent;
  text-align: left;

  &:focus-visible {
    outline: ${({ theme }) => theme.color.text.brand} auto 1px;
    outline-offset: 2px;
  }
`;

export default ReportInfo;
